// ts-rest contract for the KYC/KYB vendor adapter endpoints.
//
// Five endpoints:
//   GET  /api/integrations/kyc/status
//   POST /api/integrations/kyc/screen-individual
//   POST /api/integrations/kyc/screen-entity
//   POST /api/integrations/kyc/applicant
//   POST /api/integrations/kyc/webhook/:vendor

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Vendor enum
// ---------------------------------------------------------------------------

export const kycVendorSchema = z.enum(['comply_advantage', 'sumsub', 'smartsearch']);
export type KycVendor = z.infer<typeof kycVendorSchema>;

// ---------------------------------------------------------------------------
// Screen result — returned by screenIndividual and screenEntity
// ---------------------------------------------------------------------------

export const kycMatchSchema = z.object({
    id: z.string(),
    name: z.string(),
    matchType: z.string(), // e.g. "sanctions", "pep", "adverse_media"
    score: z.number(), // 0–100 vendor confidence
    countries: z.array(z.string()),
    detail: z.string().optional(),
});
export type KycMatch = z.infer<typeof kycMatchSchema>;

export const kycScreenStatusSchema = z.enum(['clear', 'review', 'hit']);
export type KycScreenStatus = z.infer<typeof kycScreenStatusSchema>;

export const kycScreenResultSchema = z.object({
    searchId: z.string(),
    clientId: z.string(),
    vendor: kycVendorSchema,
    status: kycScreenStatusSchema,
    matchCount: z.number(),
    matches: z.array(kycMatchSchema),
    screenedAt: z.string(), // ISO timestamp
    raw: z.unknown().optional(), // vendor-specific raw payload (omitted in demo)
});
export type KycScreenResult = z.infer<typeof kycScreenResultSchema>;

// ---------------------------------------------------------------------------
// Applicant — returned by fetchApplicant
// ---------------------------------------------------------------------------

export const kycApplicantSchema = z.object({
    applicantId: z.string(),
    clientId: z.string(),
    vendor: kycVendorSchema,
    status: z.string(), // vendor-specific status string
    reviewResult: z.string().nullable(),
    createdAt: z.string(), // ISO timestamp
    updatedAt: z.string(), // ISO timestamp
});
export type KycApplicant = z.infer<typeof kycApplicantSchema>;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const kycScreenIndividualRequestSchema = z.object({
    clientId: z.string(),
    name: z.string(),
    dateOfBirth: z.string().optional(), // ISO date
    nationality: z.string().optional(), // ISO 3166-1 alpha-2
    vendor: kycVendorSchema.optional(), // override default vendor
});
export type KycScreenIndividualRequest = z.infer<typeof kycScreenIndividualRequestSchema>;

export const kycScreenEntityRequestSchema = z.object({
    clientId: z.string(),
    companyName: z.string(),
    registrationNumber: z.string().optional(),
    countryOfRegistration: z.string().optional(), // ISO 3166-1 alpha-2
    vendor: kycVendorSchema.optional(), // override default vendor
});
export type KycScreenEntityRequest = z.infer<typeof kycScreenEntityRequestSchema>;

export const kycApplicantRequestSchema = z.object({
    applicantId: z.string(),
    vendor: kycVendorSchema,
});
export type KycApplicantRequest = z.infer<typeof kycApplicantRequestSchema>;

export const kycWebhookPayloadSchema = z.object({
    event: z.string(), // e.g. "search_completed", "applicant_reviewed"
    searchId: z.string().optional(),
    applicantId: z.string().optional(),
    data: z.unknown().optional(),
});
export type KycWebhookPayload = z.infer<typeof kycWebhookPayloadSchema>;

// ---------------------------------------------------------------------------
// Status response
// ---------------------------------------------------------------------------

export const kycVendorStatusSchema = z.object({
    configured: z.boolean(),
    isDefault: z.boolean(),
});
export type KycVendorStatus = z.infer<typeof kycVendorStatusSchema>;

export const kycStatusResponseSchema = z.object({
    vendors: z.object({
        complyAdvantage: kycVendorStatusSchema,
        sumsub: kycVendorStatusSchema,
        smartsearch: kycVendorStatusSchema,
    }),
    defaultIndividual: kycVendorSchema,
    defaultEntity: kycVendorSchema,
});
export type KycStatusResponse = z.infer<typeof kycStatusResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const kycContract = c.router(
    {
        status: {
            method: 'GET',
            path: '/integrations/kyc/status',
            responses: {
                200: kycStatusResponseSchema,
            },
            summary: 'Return KYC vendor configuration status',
        },

        screenIndividual: {
            method: 'POST',
            path: '/integrations/kyc/screen-individual',
            body: kycScreenIndividualRequestSchema,
            responses: {
                200: kycScreenResultSchema,
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
            },
            summary: 'Screen an individual client (default: ComplyAdvantage)',
        },

        screenEntity: {
            method: 'POST',
            path: '/integrations/kyc/screen-entity',
            body: kycScreenEntityRequestSchema,
            responses: {
                200: kycScreenResultSchema,
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
            },
            summary: 'Screen a corporate entity client (default: Sumsub Auto-KYB)',
        },

        fetchApplicant: {
            method: 'POST',
            path: '/integrations/kyc/applicant',
            body: kycApplicantRequestSchema,
            responses: {
                200: kycApplicantSchema,
                404: z.object({ error: z.string() }),
                502: z.object({ error: z.string(), code: z.literal('upstream_error') }),
            },
            summary: 'Fetch an applicant record from the specified vendor',
        },

        webhook: {
            method: 'POST',
            path: '/integrations/kyc/webhook/:vendor',
            pathParams: z.object({ vendor: kycVendorSchema }),
            body: kycWebhookPayloadSchema,
            responses: {
                200: z.object({ ok: z.boolean() }),
            },
            summary: 'Receive KYC vendor webhook events (idempotent)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
